import { computed, action, observable } from 'mobx';
import { ViewController } from '@estee/elc-service';
import { NumberFormatter } from '@estee/elc-formatters';
import { LoyaltyRepository, offerRedemptionType } from '~repositories/LoyaltyRepository';
import { emit, events } from '@estee/elc-service-bus';
import { ILoyaltyRPCRequest } from '~api/interfaces/ILoyaltyRpcAPI';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/LoyaltyService';
import { IRewardsContent, IRewardsInfo, IRewardPromotionOffer } from '~views/dashboard/loyalty-rewards/ILoyaltyRewards';
import Rewards from '~domain/entities/LoyaltyRewardsInfo';
import { PriorityOrderSort, CategoryOrderSort } from '~utils/OffersSorting';
import { AnalyticalTrackingUseCase } from '~use-cases/AnalyticalTrackingUseCase';
import { eventTag, ILoyaltyEventData } from '~utils/Analytics';
import { updateCartEvent } from '~internal/utils/CartUtils';

export interface IConfigParamsNumberFormats {
    groupingFormatForComma?: boolean;
    rewards: IRewardsContent;
}

class LoyaltyRewardsController extends ViewController {
    private loyaltyRepository: LoyaltyRepository;
    private numberFormatter: NumberFormatter;
    private rewardsPromotionData: IRewardPromotionOffer[] = [];

    @observable currentTabIndex: string;
    @observable private configParams: IConfigParamsNumberFormats;
    @observable rewardOfferType: string;

    constructor(loyaltyRepository: LoyaltyRepository) {
        super();
        this.loyaltyRepository = loyaltyRepository;
        this.numberFormatter = diContainer.get(serviceNames.numberFormatter);
    }

    @action
    public setConfigParams = (configParams: IConfigParamsNumberFormats) => {
        this.configParams = configParams;
    };

    @action
    public loaded = async (rewardOfferType: string) => {
        this.isLoading = true;
        await this.loyaltyRepository.fetchRewardsData(rewardOfferType);
        this.rewardOfferType = rewardOfferType;
        setTimeout(() => {
            this.triggerRewardsViewPromotionEvent();
        }, 100);
        this.isLoading = false;
    };

    public onShopNowLinkClick = (linkLabel: string) => {
        emit(events.LOYALTY_SHOP_NOW_CLICKED, linkLabel);
    };

    @action
    public handleRedeemOffer = async (applyOfferCode: ILoyaltyRPCRequest) => {
        this.isLoading = true;

        await this.loyaltyRepository.redeemOffer(
            applyOfferCode,
            this.rewardOfferType,
            offerRedemptionType.rewards
        );

        const rewardsMessageData = this.loyaltyRepository?.offerRedemptionMessageData?.rewards;
        const offerSuccessMessageFromConfig = this.configParams?.rewards?.offerSuccessMessage?.text;
        if (
            offerSuccessMessageFromConfig &&
            rewardsMessageData?.type === 'success' &&
            rewardsMessageData?.redemptionAction === 'applied'
        ) {
            this.loyaltyRepository.setOfferRedemptionMessage({
                ...rewardsMessageData,
                content: [offerSuccessMessageFromConfig]
            });
        }

        const { offerCode, offerView, tabId, offerTitle, promoPosition } = applyOfferCode;
        if (offerView === 'ChoicesOfSku') {
            const cartItemcCount = this.loyaltyRepository?.benefitsOfferInfo?.cartItemsCount;
            if (cartItemcCount) {
                emit(events.LOYALTY_REWARDS_REDEEM_CLICKED);
            }
        } else {
            const redeemOffer = this.loyaltyRepository?.benefitsOfferInfo?.inCartStatus;
            emit(events.LOYALTY_BENEFITS_REDEEM_CLICKED, offerCode);
            if (redeemOffer) {
                emit(events.LOYALTY_BENEFITS_REDEEM_CLICKED, offerCode);
            }
        }
        if (offerCode) {
            const promoCreative = tabId?.replace(/ /g, '_').toLowerCase();
            const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
                {
                    event_name: 'select_promotion',
                    enh_action: 'promo_click',
                    event_action: 'loyalty_promotion_click',
                    event_label: 'loyalty_redeem_click_rewards',
                    promo_pos: [`${promoPosition}`],
                    promo_creative: [`${promoCreative}`],
                    promo_name: [`${offerTitle?.toLocaleLowerCase()}`],
                    promo_id: [`module-${promoCreative}-${offerCode}`]
                },
                eventTag.LOYALTY_ACTIONS_TRACKING
            );
            LoyaltyEventTracking.execute();
        }

        updateCartEvent();
        this.isLoading = false;
    };

    @action
    public handleRemoveOffer = async (offerCode: string, offerView: string) => {
        this.isLoading = true;

        await this.loyaltyRepository.removeOffer(
            offerCode,
            this.rewardOfferType,
            offerRedemptionType.rewards
        );

        const rewardsMessageData = this.loyaltyRepository?.offerRedemptionMessageData?.rewards;
        const offerRemovedMessageFromConfig = this.configParams?.rewards?.offerRemovedMessage?.text;
        if (
            offerRemovedMessageFromConfig &&
            rewardsMessageData?.type === 'success' &&
            rewardsMessageData?.redemptionAction === 'removed'
        ) {
            this.loyaltyRepository.setOfferRedemptionMessage({
                ...rewardsMessageData,
                content: [offerRemovedMessageFromConfig]
            });
        }

        if (offerView === 'ChoicesOfSku') {
            emit(events.LOYALTY_REWARDS_REMOVE_CLICKED);
        } else {
            emit(events.LOYALTY_BENEFITS_REMOVE_CLICKED, offerCode);
        }
        if (offerCode) {
            const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
                { event_name: 'loyalty_rewards', event_action: 'reward_removed', event_label: offerCode },
                eventTag.LOYALTY_ACTIONS_TRACKING
            );
            LoyaltyEventTracking.execute();
        }

        updateCartEvent();
        this.isLoading = false;
    };

    @action
    public setCurrentTabId = (tabId: string) => {
        this.currentTabIndex = tabId;

        emit(events.LOYALTY_REWARDS_TAB_CLICKED, tabId);
        const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
            { event_name: 'loyalty_rewards', event_action: 'section_title_click', event_label: tabId },
            eventTag.LOYALTY_ACTIONS_TRACKING
        );
        LoyaltyEventTracking.execute();
        this.triggerRewardsViewPromotionEvent();
    };

    @computed
    get currentTabId() {
        return this.currentTabIndex;
    }

    @computed
    public get formattedPointsValue() {
        const { totalRedeemedPoints = 0 } = this.loyaltyRepository?.rewardsInfo || {};
        const pointsValue =
            this.loyaltyRepository?.loyaltyInfo?.currentPoints - totalRedeemedPoints;

        const { groupingFormatForComma } = this.configs;

        return this.numberFormatter.formatNumber(pointsValue, {
            shouldUseGrouping: groupingFormatForComma
        });
    }

    @action
    public formattedCurrentPoints(value: { text: string }, currentPoints: string) {
        return value?.text && currentPoints
            ? value.text.replace('::currentPoints::', currentPoints)
            : '';
    }

    @action
    public onRewardViewDetailsClick(eLabel: string) {
        const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
            { event_name: 'loyalty_rewards', event_action: 'cta_click', event_label: eLabel },
            eventTag.LOYALTY_ACTIONS_TRACKING
        );
        LoyaltyEventTracking.execute();
    }

    public onRewardsTitleClick = (promoPosition: number, tabId: string) => {
        const promoData = this.rewardsPromotionData.find(promotionData => promotionData.promoPosition === promoPosition && promotionData.tabId === tabId);

        if (promoData?.offerCode) {
            const promoCreative = tabId?.replace(/ /g, '_').toLowerCase();
            const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
                {
                    event_name: 'select_promotion',
                    event_action: 'promotion click',
                    enh_action: 'promo_click',
                    event_label: promoData.offerCode,
                    promo_pos: [`${promoPosition}`],
                    promo_creative: [promoCreative],
                    promo_name: [`${promoData.productName?.toLocaleLowerCase()}`],
                    promo_id: [`module-benefits-${promoData.offerCode}`]
                },
                eventTag.LOYALTY_ACTIONS_TRACKING
            );
            LoyaltyEventTracking.execute();
        }
    };

    @action
    public setRewardsViewPromotionData = (sliderItem: IRewardsInfo, offerMessage: string, tabId: string, promoPosition: number) => {
        const { offerCode, meetsCriteria, skuID, productName } = sliderItem;

        this.rewardsPromotionData?.push({ offerCode, meetsCriteria, skuID, tabId, productName, offerMessage, promoPosition});
    };

    @action
    public triggerRewardsViewPromotionEvent = () => {
        const { tierLevels } = this.configs.rewards;
        let tabId = '';
        if (this.currentTabId) {
            tabId = this.currentTabId;
        } else if (tierLevels && tierLevels.length > 0) {
            tabId = `${tierLevels[0].label}`;
        }

        const eventData: ILoyaltyEventData = {
            event_name: 'view_promotion',
            event_category: 'ecommerce',
            event_action: 'loyalty_promo_impression',
            event_label: 'loyalty_promo_impression',
            promo_impression_list: [],
            promo_impression_offer_code: [],
            promo_impression_name: [],
            promo_impression_status: [],
            promo_impression_message: [],
            promo_impression_sku: [],
            promo_impression_points: []
        };

        this.rewardsPromotionData.forEach( (promotion) => {
            const offerStatus = promotion.meetsCriteria ? 'active' : 'disbaled';

            if (promotion.tabId === tabId ) {
                eventData.promo_impression_list?.push('rewards');
                eventData.promo_impression_offer_code?.push(`${promotion.offerCode}`);
                eventData.promo_impression_name?.push(`${promotion.productName}`);
                eventData.promo_impression_status?.push(`${offerStatus}`);
                eventData.promo_impression_message?.push(`${promotion.offerMessage}`);
                eventData.promo_impression_sku?.push(`${promotion.skuID}`);
                eventData.promo_impression_points?.push(`${promotion.tabId}`);
            }
        });

        const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
            eventData,
            eventTag.LOYALTY_ACTIONS_TRACKING
        );
        LoyaltyEventTracking.execute();
    };

    @action
    public onRewardsArrowClick(eLabel: string) {
        const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
            { event_name: 'loyalty_rewards', event_action: 'arrow_click', event_label: eLabel },
            eventTag.LOYALTY_ACTIONS_TRACKING
        );
        LoyaltyEventTracking.execute();
    }

    @computed
    public get formattedPointsInfoDescription() {
        const { rewardsPointsInfo } = this.configs.rewards;

        return this.formattedCurrentPoints(rewardsPointsInfo, this.formattedPointsValue);
    }

    @computed
    public get shouldDisplayInsufficientPointsSubHeader() {
        const { tierLevels } = this.configs.rewards;
        const { totalRedeemedPoints = 0 } = this.loyaltyRepository?.rewardsInfo || {};
        const currentPoints = this.loyaltyRepository?.loyaltyInfo?.currentPoints - totalRedeemedPoints;

        if (tierLevels && tierLevels.length > 0) {
            return currentPoints < tierLevels[0].points;
        } else {
            return false;
        }
    }

    @computed
    public get formattedRewardsDescription() {
        const { rewardsSubHeader, rewardsSubHeaderInsufficientPoints } = this.configs.rewards;

        if (rewardsSubHeaderInsufficientPoints && this.shouldDisplayInsufficientPointsSubHeader) {
            return this.formattedCurrentPoints(rewardsSubHeaderInsufficientPoints, this.formattedPointsValue);
        } else {
            return this.formattedCurrentPoints(rewardsSubHeader, this.formattedPointsValue);
        }
    }

    @computed
    public get data() {
        let rewardsInfo: IRewardsInfo[] = [];
        if (this.configParams?.rewards.sorting?.enable && this.loyaltyRepository?.rewardsInfo?.rewardsDataFormatted) {
            const { priorityOrder, categoryOrder } = this.configParams.rewards.sorting;
            rewardsInfo = Rewards.getSortedRewardsInfo(this.loyaltyRepository.rewardsInfo.rewardsDataFormatted,
                this.loyaltyRepository.rewardsInfo.totalRedeemedPoints,
                priorityOrder as PriorityOrderSort[], categoryOrder as CategoryOrderSort[]).rewardsDataFormatted;
        } else {
            rewardsInfo = this.loyaltyRepository?.rewardsInfo?.rewardsDataFormatted;
        }

        return {
            rewardsInfo,
            rewardsOfferInfo: this.loyaltyRepository?.benefitsOfferInfo,
            birthdayRedeemed: this.loyaltyRepository?.isBirthdayRedeemed,
            offerRedemptionMessages: this.loyaltyRepository?.offerRedemptionMessageData,
            isLoading: this.isLoading
        };
    }

    @computed
    public get configs() {
        return this.configParams;
    }
}

export default LoyaltyRewardsController;
